/**
 * ExtendedTextNode - Preserves inline styles (color, background-color, font-size, font-family, etc.)
 * when pasting HTML content into the Lexical editor.
 *
 * Ported from the backend ExtendedTextNode.js, adapted for frontend use with LexicalNodeReplacement.
 */

import {
  $isTextNode,
  type DOMConversion,
  type DOMConversionMap,
  type DOMConversionOutput,
  type LexicalNode,
  type SerializedTextNode,
  TextNode,
} from 'lexical';

const PRESERVED_STYLE_PROPERTIES = [
  'color',
  'background-color',
  'font-size',
  'font-family',
  'line-height',
  'letter-spacing',
  'word-spacing',
  'text-align',
  'text-transform',
  'white-space',
  'text-shadow',
  'opacity',
  'vertical-align',
  'font-weight',
  'text-decoration',
] as const;

/**
 * Merges CSS style strings, deduplicating properties (new styles take precedence)
 */
function mergeStyles(existingStyles: string, newStyles: string): string {
  const styleMap: Record<string, string> = {};

  if (existingStyles) {
    for (const declaration of existingStyles.split(';')) {
      const [prop, ...valueParts] = declaration.split(':');
      const trimmedProp = prop?.trim();
      const value = valueParts.join(':')?.trim();
      if (trimmedProp && value) {
        styleMap[trimmedProp.toLowerCase()] = value;
      }
    }
  }

  if (newStyles) {
    for (const declaration of newStyles.split(';')) {
      const [prop, ...valueParts] = declaration.split(':');
      const trimmedProp = prop?.trim();
      const value = valueParts.join(':')?.trim();
      if (trimmedProp && value) {
        styleMap[trimmedProp.toLowerCase()] = value;
      }
    }
  }

  return Object.entries(styleMap)
    .map(([prop, value]) => `${prop}: ${value}`)
    .join('; ');
}

/**
 * Extracts preserved CSS properties from a DOM element's inline style
 */
function extractPreservedStyles(element: HTMLElement): string {
  const styles: string[] = [];

  for (const property of PRESERVED_STYLE_PROPERTIES) {
    const value = element.style.getPropertyValue(property);
    const priority = element.style.getPropertyPriority(property);
    if (value) {
      const prioritySuffix = priority ? ' !important' : '';
      styles.push(`${property}: ${value}${prioritySuffix}`);
    }
  }

  return styles.join('; ');
}

/** Known CSS named colors for <font color="..."> validation */
const CSS_NAMED_COLORS = new Set([
  'black',
  'silver',
  'gray',
  'white',
  'maroon',
  'red',
  'purple',
  'fuchsia',
  'green',
  'lime',
  'olive',
  'yellow',
  'navy',
  'blue',
  'teal',
  'aqua',
  'aliceblue',
  'antiquewhite',
  'aquamarine',
  'azure',
  'beige',
  'bisque',
  'blanchedalmond',
  'blueviolet',
  'brown',
  'burlywood',
  'cadetblue',
  'chartreuse',
  'chocolate',
  'coral',
  'cornflowerblue',
  'cornsilk',
  'crimson',
  'cyan',
  'darkblue',
  'darkcyan',
  'darkgoldenrod',
  'darkgray',
  'darkgreen',
  'darkgrey',
  'darkkhaki',
  'darkmagenta',
  'darkolivegreen',
  'darkorange',
  'darkorchid',
  'darkred',
  'darksalmon',
  'darkseagreen',
  'darkslateblue',
  'darkslategray',
  'darkslategrey',
  'darkturquoise',
  'darkviolet',
  'deeppink',
  'deepskyblue',
  'dimgray',
  'dimgrey',
  'dodgerblue',
  'firebrick',
  'floralwhite',
  'forestgreen',
  'gainsboro',
  'ghostwhite',
  'gold',
  'goldenrod',
  'greenyellow',
  'grey',
  'honeydew',
  'hotpink',
  'indianred',
  'indigo',
  'ivory',
  'khaki',
  'lavender',
  'lavenderblush',
  'lawngreen',
  'lemonchiffon',
  'lightblue',
  'lightcoral',
  'lightcyan',
  'lightgoldenrodyellow',
  'lightgray',
  'lightgreen',
  'lightgrey',
  'lightpink',
  'lightsalmon',
  'lightseagreen',
  'lightskyblue',
  'lightslategray',
  'lightslategrey',
  'lightsteelblue',
  'lightyellow',
  'limegreen',
  'linen',
  'magenta',
  'mediumaquamarine',
  'mediumblue',
  'mediumorchid',
  'mediumpurple',
  'mediumseagreen',
  'mediumslateblue',
  'mediumspringgreen',
  'mediumturquoise',
  'mediumvioletred',
  'midnightblue',
  'mintcream',
  'mistyrose',
  'moccasin',
  'navajowhite',
  'oldlace',
  'olivedrab',
  'orange',
  'orangered',
  'orchid',
  'palegoldenrod',
  'palegreen',
  'paleturquoise',
  'palevioletred',
  'papayawhip',
  'peachpuff',
  'peru',
  'pink',
  'plum',
  'powderblue',
  'rosybrown',
  'royalblue',
  'saddlebrown',
  'salmon',
  'sandybrown',
  'seagreen',
  'seashell',
  'sienna',
  'skyblue',
  'slateblue',
  'slategray',
  'slategrey',
  'snow',
  'springgreen',
  'steelblue',
  'tan',
  'thistle',
  'tomato',
  'turquoise',
  'violet',
  'wheat',
  'whitesmoke',
  'yellowgreen',
  'rebeccapurple',
  'transparent',
  'currentcolor',
]);

function isValidCssColor(value: string): boolean {
  const trimmed = value.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return true;
  if (/^rgba?\(\s*[\d\s,%./]+\)$/.test(trimmed)) return true;
  if (CSS_NAMED_COLORS.has(trimmed.toLowerCase())) return true;
  return false;
}

function sanitizeFontFamily(face: string): string {
  return face
    .split(',')
    .map((f) => f.trim())
    .filter((f) => f.length > 0 && /^[a-zA-Z0-9\s-]+$/.test(f))
    .map((f) => `"${f}"`)
    .join(', ');
}

/**
 * Converts legacy <font> tag attributes (color, face, size) to CSS properties.
 * Validates and sanitizes attribute values before building styles.
 */
function extractFontTagStyles(fontElement: HTMLElement): string {
  const styles: string[] = [];

  const color = fontElement.getAttribute('color');
  if (color && isValidCssColor(color)) {
    styles.push(`color: ${color.trim()}`);
  }

  const face = fontElement.getAttribute('face');
  if (face) {
    const sanitized = sanitizeFontFamily(face);
    if (sanitized) {
      styles.push(`font-family: ${sanitized}`);
    }
  }

  const size = fontElement.getAttribute('size');
  if (size) {
    const sizeMap: Record<string, string> = {
      '1': 'x-small',
      '2': 'small',
      '3': 'medium',
      '4': 'large',
      '5': 'x-large',
      '6': 'xx-large',
      '7': 'xxx-large',
    };

    const fontSize = sizeMap[size];
    if (fontSize) {
      styles.push(`font-size: ${fontSize}`);
    } else {
      const relativeMatch = size.match(/^([+-])(\d+)$/);
      if (relativeMatch) {
        const [, sign, steps] = relativeMatch;
        const numSteps = Number.parseInt(steps ?? '0', 10);
        if (!Number.isNaN(numSteps) && numSteps >= 0 && numSteps <= 7) {
          const multiplier = sign === '+' ? 1.2 ** numSteps : (1 / 1.2) ** numSteps;
          const percentage = Math.min(Math.max(Math.round(multiplier * 100), 50), 300);
          styles.push(`font-size: ${percentage}%`);
        }
      }
    }
  }

  return styles.join('; ');
}

/**
 * Wraps an existing DOM conversion function to also extract and apply inline styles.
 * Falls back to a passthrough conversion when the base converter returns null,
 * ensuring inline styles are still captured from the DOM element.
 */
function patchStyleConversion(
  originalConversion: ((node: HTMLElement) => DOMConversionOutput | null) | undefined
): DOMConversion<HTMLElement> {
  return {
    conversion: (node: HTMLElement): DOMConversionOutput | null => {
      const originalOutput = originalConversion ? originalConversion(node) : null;

      let inlineStyles = extractPreservedStyles(node);

      if (node.tagName === 'FONT') {
        const fontStyles = extractFontTagStyles(node);
        if (fontStyles) {
          inlineStyles = inlineStyles ? mergeStyles(inlineStyles, fontStyles) : fontStyles;
        }
      }

      if (!originalOutput && !inlineStyles) {
        return null;
      }

      if (!inlineStyles) {
        return originalOutput;
      }

      return {
        ...(originalOutput ?? { node: null }),
        forChild: (lexicalNode, parentLexicalNode) => {
          const originalForChild = originalOutput?.forChild ?? ((x: LexicalNode) => x);
          const result = originalForChild(lexicalNode, parentLexicalNode);

          if ($isTextNode(result)) {
            const existingStyles = result.getStyle();
            const mergedStyles = existingStyles
              ? mergeStyles(existingStyles, inlineStyles)
              : inlineStyles;
            if (mergedStyles) {
              result.setStyle(mergedStyles);
            }
          }

          return result;
        },
      };
    },
  };
}

export class ExtendedTextNode extends TextNode {
  static getType(): string {
    return 'extended-text';
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  isSimpleText(): boolean {
    return (this.__type === 'text' || this.__type === 'extended-text') && this.__mode === 0;
  }

  static importDOM(): DOMConversionMap | null {
    const importers = TextNode.importDOM();

    return {
      ...importers,
      span: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.span?.(node)?.conversion),
        priority: 1,
      }),
      b: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.b?.(node)?.conversion),
        priority: 1,
      }),
      strong: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.strong?.(node)?.conversion),
        priority: 1,
      }),
      em: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.em?.(node)?.conversion),
        priority: 1,
      }),
      i: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.i?.(node)?.conversion),
        priority: 1,
      }),
      u: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.u?.(node)?.conversion),
        priority: 1,
      }),
      s: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.s?.(node)?.conversion),
        priority: 1,
      }),
      del: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.del?.(node)?.conversion),
        priority: 1,
      }),
      sub: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.sub?.(node)?.conversion),
        priority: 1,
      }),
      sup: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.sup?.(node)?.conversion),
        priority: 1,
      }),
      mark: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.mark?.(node)?.conversion),
        priority: 1,
      }),
      font: (node: HTMLElement) => ({
        ...patchStyleConversion(importers?.font?.(node)?.conversion),
        priority: 1,
      }),
    };
  }

  static importJSON(serializedNode: SerializedTextNode): ExtendedTextNode {
    const node = new ExtendedTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style || '');
    return node;
  }

  exportJSON(): SerializedTextNode {
    return super.exportJSON();
  }
}
